/**
 * Das Positionen-Popup einer Zeitstrahl-Zeile - zeigt ALLE Positionen einer Bestellung (keine
 * Kappung wie im Artikelstreifen), geoeffnet vom Artikelstreifen (`Artikelstreifen.tsx`).
 *
 * OHNE VORBILD IM ORIGINAL (Auftrag 2). Uebernimmt das Overlay-Muster von `ArtikelbildOverlay`
 * (`components/Artikelbild.tsx`, Kopfkommentar dort fuer die Herkunft dieses Musters):
 * `createPortal` nach `document.body`, `role="dialog"` + `aria-modal="true"`, Escape schliesst,
 * Fokusfalle, Klick auf den Hintergrund schliesst. Die Fokus-RUECKGABE an den Streifen-Knopf liegt
 * beim Aufrufer (`Artikelstreifen.tsx`, wie bei `Artikelbild.tsx#schliessenUndFokusZurueck`) - diese
 * Komponente kennt den ausloesenden Knopf nicht.
 *
 * Wiederverwendet werden `.board-editor-overlay`/`.board-editor-dialog`/`.board-editor-head`/
 * `.board-editor-head-copy`/`.board-editor-body`/`.board-editor-list`/`.board-editor-foot`/
 * `.icon-btn` (`styles/bestand.css:246ff.`, genau wie `Users/PasswortDialog.tsx`) - `bestand.css`
 * muss zeichengleich zum `<style>`-Block aus `index.html` bleiben (`test/reference/stylesheet.test.ts`),
 * darum stehen NUR die Klassen, die die Positionsliste zusaetzlich braucht, in
 * `styles/neubau/zeitstrahlZeile.css` (dasselbe Vorgehen wie `styles/neubau/artikelbild.css`,
 * Kopfkommentar dort: ".item-image-head/-cell ... hier statt dort, weil bestand.css ... keine
 * Entsprechung hat").
 *
 * `.board-editor-body` traegt bereits `overflow: auto` (der Dialog selbst `overflow: hidden`) -
 * genau die geforderte Trennung "scrollt, ohne das Fenster zu sprengen" (Auftragstext), ohne dass
 * eine eigene Regel dafuer noetig waere.
 *
 * ================= Verschachtelte Portale: Grossbild zuerst schliessen =================
 *
 * Jedes Bild ist hier `klickbar` (Vorgabe von `Artikelbild`, `true`) - ein Klick oeffnet das
 * Grossbild-Overlay von `Artikelbild.tsx` als ZWEITES, GESCHWISTERLICHES Portal an `document.body`.
 * Dessen eigener Escape-Listener (`document.addEventListener("keydown", ...)`) schliesst
 * ausschliesslich sich selbst und ruft dabei kein `stopPropagation` - ohne Gegenmassnahme wuerde
 * EIN Escape-Druck deshalb BEIDE Overlays gleichzeitig schliessen (dieser Listener ist zuerst
 * registriert, liefe also zuerst und raeumte das Popup samt Grossbild in einem Schritt ab).
 *
 * Der Escape-Handler unten prueft deshalb `document.activeElement`: das Grossbild-Overlay holt sich
 * beim Oeffnen den Fokus in SEINEN EIGENEN Dialog (`ArtikelbildOverlay`, `useLayoutEffect`) - liegt
 * der Fokus nicht (mehr) innerhalb DIESES Dialogs, ist ein anderes, spaeter geoeffnetes Overlay
 * dran, und dieses Popup ruehrt sich nicht. Erst ein zweiter Escape-Druck (nachdem das Grossbild
 * bereits zu ist und der Fokus wieder hier liegt) schliesst das Popup selbst.
 *
 * ================= NACHBESSERUNG (K1): jeder Klick im Popup oeffnete zusaetzlich den Drawer =================
 *
 * RICHTIGGESTELLT (Feinschliff Punkt 4, Pruefung 5648ff6 - vorherige Fassung dieses Kommentars
 * benannte die Kette falsch): `PositionenPopup` ist im React-Baum KEIN Kind des Streifen-Knopfes,
 * sondern dessen GESCHWISTER - Knopf und (bedingt gerendertes) Popup stehen als zwei direkte Kinder
 * IM SELBEN Fragment `<>...</>` (`Artikelstreifen.tsx:52-91`). Die Kette ist deshalb: Popup-Wurzel
 * (dieser Datei) -> Fragment von `Artikelstreifen.tsx` (reicht Ereignisse nur durch, ist selbst kein
 * DOM-Knoten) -> `.tlp-row` mit `onClick={() => onOeffnen(order.id)}` (`ZeitstrahlSeite.tsx`). Der
 * Streifen-KNOPF selbst steht NICHT auf diesem Pfad - sein eigenes `stopPropagation`
 * (`Artikelstreifen.tsx`, haelt nur den Klick auf den Knopf SELBST auf) war fuer Klicks INNERHALB
 * des Popups nie im Spiel, weder vorher noch nachher.
 *
 * Was tatsaechlich stimmt (unveraendert): Portale (`createPortal`) verlegen nur, WO ein Knoten im
 * DOM haengt - Ereignisse blasen weiterhin den REACT-Baum hinauf, nicht den DOM-Baum (React-
 * Dokumentation zu Portalen, "Event Bubbling Through Portals"). Ein Klick IRGENDWO im Popup -
 * Hintergrund, Schliessen-Knopf, ein Positionsbild, sogar ein Klick im verschachtelten
 * Grossbild-Overlay von `Artikelbild` (selbst ein REACT-Kind einer Positionszeile hier) - lief
 * bislang unveraendert ueber das Fragment weiter bis zur `.tlp-row` und oeffnete den Drawer MIT.
 *
 * Der Riegel gehoert an die WURZEL des Popups (`beiKlick` unten, ehemals `beiHintergrundKlick`): dort
 * laufen ausnahmslos ALLE Klicks aus diesem Teilbaum zusammen, bevor sie ueber das Fragment die Zeile
 * ueberhaupt erreichen koennten - `stopPropagation()` steht deshalb VOR der Hintergrund-Pruefung,
 * nicht nur in deren `true`-Zweig.
 */
import { useEffect, useLayoutEffect, useRef, type JSX, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import { formatPositionenZusammenfassung } from "../../lib/positionenSumme.ts";
import type { Order, OrderItem } from "../../lib/types.ts";
import { Artikelbild } from "../Artikelbild.tsx";
import { Ikone } from "../Ikone.tsx";

export interface PositionenPopupProps {
  order: Order;
  onSchliessen: () => void;
}

/** Die im Dialog fokussierbaren Elemente, fuer die Fokusfalle unten. */
function fokussierbareElemente(dialog: HTMLElement): HTMLElement[] {
  return Array.from(
    dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), ' +
        'textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
    ),
  );
}

export function PositionenPopup({ order, onSchliessen }: PositionenPopupProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const items = Array.isArray(order.items) ? (order.items as OrderItem[]) : [];
  const orderNo = (order.orderNo as string) || "—";
  const orderName = (order.name as string) || "Bestellung";
  const summe = formatPositionenZusammenfassung(items);

  // Fokus beim Oeffnen in den Dialog - dieselbe Herangehensweise wie `ArtikelbildOverlay`.
  useLayoutEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (ereignis.key === "Escape") {
        // Siehe Kopfkommentar "Verschachtelte Portale": ein verschachteltes Grossbild-Overlay haelt
        // den Fokus in SEINEM Dialog - liegt er nicht in UNSEREM, ist das Grossbild dran, nicht wir.
        if (dialogRef.current && !dialogRef.current.contains(document.activeElement)) return;
        onSchliessen();
        return;
      }
      if (ereignis.key !== "Tab" || !dialogRef.current) return;
      // Echte Fokusfalle (NICHT das Ein-Element-Muster von `ArtikelbildOverlay`): diese Liste hat in
      // der Regel MEHRERE fokussierbare Elemente (Schliessen-Knopf plus je ein Bildknopf), Tab muss
      // also innerhalb der Liste weiterlaufen und nur an den ENDEN umspringen.
      const elemente = fokussierbareElemente(dialogRef.current);
      if (!elemente.length) return;
      const erstes = elemente[0];
      const letztes = elemente[elemente.length - 1];
      if (ereignis.shiftKey && document.activeElement === erstes) {
        ereignis.preventDefault();
        letztes.focus();
      } else if (!ereignis.shiftKey && document.activeElement === letztes) {
        ereignis.preventDefault();
        erstes.focus();
      }
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  }, [onSchliessen]);

  function beiKlick(ereignis: ReactMouseEvent<HTMLDivElement>): void {
    // NACHBESSERUNG (K1): siehe Kopfkommentar - MUSS vor der Hintergrund-Pruefung stehen, damit sie
    // auch fuer Klicks GREIFT, die NICHT den Hintergrund treffen (Schliessen-Knopf, Positionsbild,
    // das verschachtelte Grossbild-Overlay).
    ereignis.stopPropagation();
    if (ereignis.target === ereignis.currentTarget) onSchliessen();
  }

  return createPortal(
    <div className="board-editor-overlay open" role="presentation" onClick={beiKlick}>
      <div
        ref={dialogRef}
        className="board-editor-dialog positionen-popup-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Positionen der Bestellung ${orderNo}`}
        tabIndex={-1}
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2>{orderName}</h2>
            <p>
              {orderNo}
              {order.supplier ? ` · ${String(order.supplier)}` : ""}
            </p>
          </div>
          <button className="icon-btn" type="button" onClick={onSchliessen} aria-label="Schließen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body">
          <div className="board-editor-list positionen-popup-liste">
            {items.map((item, index) => {
              const menge = Number(item?.qty);
              // NACHBESSERUNG (Geringes 2): dieselbe Regel wie am Mengen-Badge im Streifen
              // (`Artikelstreifen.tsx`) - vorher zeigte das Popup bei einem nicht-numerischen `qty`
              // (z. B. "k. A.") den Rohwert, der Streifen unterdrueckte dort seinen Badge ganz. Zwei
              // Regeln fuer denselben Wert; jetzt EINE.
              const zeigeMenge = Number.isFinite(menge) && menge > 0;
              return (
                <div
                  className="positionen-popup-zeile"
                  // NACHBESSERUNG (Feinschliff Punkt 2, Pruefung 5648ff6): siehe Kommentar an
                  // derselben Stelle in `Artikelstreifen.tsx` - getrennte Schluesselraeume statt
                  // einer Kollision zwischen `kLieferantenBestellungPos` und `index`.
                  key={
                    item?.kLieferantenBestellungPos != null
                      ? `pos-${item.kLieferantenBestellungPos}`
                      : `idx-${index}`
                  }
                >
                  <Artikelbild kArtikel={item?.kArtikel} name={item?.name} hatBild={item?.hatBild} />
                  <div className="positionen-popup-info">
                    <div className="positionen-popup-name">{(item?.name as string) || ""}</div>
                    <div className="positionen-popup-sku">{(item?.sku as string) || ""}</div>
                  </div>
                  <div className="positionen-popup-menge">{zeigeMenge ? menge : ""}</div>
                </div>
              );
            })}
          </div>
        </div>
        {summe ? <div className="board-editor-foot positionen-popup-summe">{summe}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
